import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Gift, Copy, Check, Users, Clock, Ticket, Crown } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CoachVouchers = () => {
  const { getAuthHeaders, user } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);
  const [error, setError] = useState(null);
  
  // Check if user is PRO from user context first
  const isPro = user?.subscription?.plan === 'pro';

  useEffect(() => {
    if (isPro) {
      fetchVouchers();
    } else {
      setLoading(false);
    }
  }, [isPro]);

  const fetchVouchers = async () => {
    setError(null);
    try {
      const response = await axios.get(`${API}/api/vouchers/my-vouchers`, {
        headers: getAuthHeaders()
      });
      setVouchers(response.data.vouchers || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      setError('Failed to load vouchers. Please try again.');
      toast.error('Failed to load vouchers');
    }
    setLoading(false);
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Voucher code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'used': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'expired': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl uppercase mb-2">Promotional Vouchers</h1>
          <p className="text-muted-foreground">Share vouchers with athletes for free trial access</p>
        </div>

        <div className="bg-gradient-to-br from-amber-500/10 to-primary/10 border border-amber-500/30 rounded-sm p-8 text-center">
          <Crown className="mx-auto text-amber-400 mb-4" size={48} />
          <h2 className="font-heading text-xl uppercase mb-2">Pro Feature</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Upgrade to Pro to receive <span className="text-primary font-semibold">20 promotional vouchers</span> to 
            attract your first athletes to the platform. Each voucher gives 1 month free of Athlete Pass.
          </p>
          <Button className="bg-amber-500 text-black hover:bg-amber-400">
            Upgrade to Pro
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl uppercase mb-2">Promotional Vouchers</h1>
          <p className="text-muted-foreground">Share vouchers with athletes for free trial access</p>
        </div>

        <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-8 text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={fetchVouchers} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl uppercase mb-2">Promotional Vouchers</h1>
        <p className="text-muted-foreground">Share vouchers with athletes for 1 month free Athlete Pass</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border/40 rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-sm">
              <Ticket className="text-primary" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total || 0}</p>
              <p className="text-xs text-muted-foreground">Total Vouchers</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/40 rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-sm">
              <Gift className="text-green-400" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">{stats.available || 0}</p>
              <p className="text-xs text-muted-foreground">Available</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/40 rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-sm">
              <Users className="text-blue-400" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-400">{stats.used || 0}</p>
              <p className="text-xs text-muted-foreground">Used</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/40 rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/10 rounded-sm">
              <Clock className="text-red-400" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">{stats.expired || 0}</p>
              <p className="text-xs text-muted-foreground">Expired</p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
        <h3 className="font-semibold text-primary mb-2">How to use vouchers:</h3>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• Share the voucher code with an athlete or parent</li>
          <li>• They can redeem it during signup for 1 month free Athlete Pass</li>
          <li>• Each voucher can only be used once, by one athlete</li>
          <li>• Vouchers expire in 90 days from creation</li>
        </ul>
      </div>

      {/* Vouchers List */}
      <div className="bg-card border border-border/40 rounded-sm">
        <div className="p-4 border-b border-border/40">
          <h2 className="font-heading text-lg uppercase">Your Vouchers</h2>
        </div>
        
        <div className="divide-y divide-border/40">
          {vouchers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No vouchers yet. Contact support to get your promotional vouchers.
            </div>
          ) : (
            vouchers.map((voucher) => (
              <div key={voucher.id} className="p-4 flex items-center justify-between hover:bg-muted/10">
                <div className="flex items-center gap-4">
                  <div className="font-mono text-lg font-bold tracking-wider">
                    {voucher.code}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(voucher.status)}`}>
                    {voucher.status}
                  </span>
                </div>
                
                <div className="flex items-center gap-4">
                  {voucher.used_at && (
                    <span className="text-xs text-muted-foreground">
                      Used: {new Date(voucher.used_at).toLocaleDateString()}
                    </span>
                  )}
                  
                  {voucher.status === 'available' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(voucher.code)}
                      className="gap-2"
                    >
                      {copiedCode === voucher.code ? (
                        <><Check size={14} /> Copied!</>
                      ) : (
                        <><Copy size={14} /> Copy</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachVouchers;
